import type { ComponentType } from 'react';
import React, { PureComponent } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import type { FlagProps } from '@atlaskit/flag/types';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Flag from '@atlaskit/flag/flag';
import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import FlagGroup from '@atlaskit/flag/flag-group';

import { type MaybeChoreographedComponentProps } from '@confluence/choreographer-services/entry-points/ipm-choreographer.ts';
import { ChoreographerAssetsContextConsumer } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { fg } from '@confluence/feature-gating';

import { FlagIcon } from './FlagIcon.tsx';
import type { FlagAction, FlagDescriptor } from './FlagsStateContainer.ts';

type FlagsComponentAction = Omit<FlagAction, 'content'> &
	(
		| {
				content: FlagAction['content'];
		  }
		| {
				contentKey: string;
		  }
	);

export type FlagsComponentProps = {
	flags: FlagDescriptor[];
	areFlagsVisible: boolean;

	/**
	 * Function to bridge atlaskit flags to AUI
	 */
	applyAJSFlagOverride?(showFlagFn: Function): void;
	removeAJSFlagOverride?(): void;

	hideFlag?(flagId: string | number): void;
	showFlag?(flag: FlagDescriptor): void;
};

export const FlagsComponent: ComponentType<FlagsComponentProps> = injectIntl(
	class FlagsComponent extends PureComponent<FlagsComponentProps & WrappedComponentProps> {
		componentDidMount() {
			const { showFlag, applyAJSFlagOverride } = this.props;

			if (showFlag) {
				applyAJSFlagOverride?.(showFlag);
			}

			this.notifyTopVisibleFlag();
		}

		componentDidUpdate(previousProps: FlagsComponentProps & WrappedComponentProps) {
			this.notifyTopVisibleFlag(previousProps);
		}

		componentWillUnmount() {
			this.props.removeAJSFlagOverride?.();
		}

		private notifyTopVisibleFlag(
			previousProps?: FlagsComponentProps & WrappedComponentProps,
		): void {
			if (!fg('twcg-2202-flag-on-shown')) {
				return;
			}

			const { areFlagsVisible, flags } = this.props;
			const topFlag = areFlagsVisible ? flags[0] : undefined;
			const previousTopFlag = previousProps?.areFlagsVisible ? previousProps.flags[0] : undefined;

			if (topFlag && topFlag.id !== previousTopFlag?.id) {
				topFlag.onShown?.();
			}
		}

		translateActions(actions: FlagsComponentAction[]): FlagAction[] {
			const { intl } = this.props;

			return actions.map((action) => {
				if ('contentKey' in action) {
					const i18nMessage = {
						id: action.contentKey,
					};
					return {
						...action,
						content: intl.formatMessage(i18nMessage),
					};
				}

				return action;
			});
		}

		onDismissed = (flagId: string | number) => {
			const { flags, hideFlag } = this.props;

			flags.forEach((flag) => {
				if (flag.id === flagId && typeof flag.onClose === 'function') {
					flag.onClose();
				}
			});

			hideFlag?.(flagId);
		};

		private toFlagProps({
			id,
			title = '',
			description = '',
			actions = [],
			type,
			appearance,
			customIcon,
			messageId,
			messageType,
			onMessageDisposition,
			delayAnnouncement,
			autoDismissSeconds,
		}: FlagDescriptor): FlagProps & MaybeChoreographedComponentProps {
			// A flag is only "bold" (solid colored background, icon shown in currentColor) when
			// its appearance is one of the bold AtlasKit appearances. The 'normal' appearance is
			// explicitly NOT bold, so its icon must keep its own semantic color (e.g. green for
			// success). `Boolean(appearance)` mistakenly treats 'normal' as bold, which forced the
			// icon to currentColor (grey). See CCCEMAU-2975.
			const isBold = fg('cc_mohiti_flag_anchoring')
				? appearance !== undefined && appearance !== 'normal'
				: Boolean(appearance);
			return {
				icon: <FlagIcon type={type} isBold={isBold} customIcon={customIcon} />,
				appearance,
				id,
				title,
				description,
				messageId,
				messageType,
				onMessageDisposition,
				autoDismissSeconds,
				actions: this.translateActions(actions),
				...(delayAnnouncement && { delayAnnouncement }),
			};
		}

		render() {
			const { flags, areFlagsVisible } = this.props;

			return (
				<ChoreographerAssetsContextConsumer>
					{(choreographerAssets) => (
						<FlagGroup onDismissed={this.onDismissed}>
							{flags.map((flagDescriptor) => {
								const FlagComponentType: ComponentType<FlagProps> =
									flagDescriptor.customComponent ||
									(flagDescriptor.close === 'auto'
										? (choreographerAssets?.AutoDismissFlag ?? AutoDismissFlag)
										: (choreographerAssets?.Flag ?? Flag));

								return (
									<div
										data-test-id="flag-visibility-wrapper"
										key={flagDescriptor.id}
										style={{ visibility: areFlagsVisible ? 'visible' : 'hidden' }}
										aria-hidden={!areFlagsVisible}
									>
										<FlagComponentType {...this.toFlagProps(flagDescriptor)} />
									</div>
								);
							})}
						</FlagGroup>
					)}
				</ChoreographerAssetsContextConsumer>
			);
		}
	},
);
